import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  getSideConversations,
  getSideConversationDetails,
  getIncidentsBySideConversation,
  getIncidentDetails,
  deleteSideConversation,
  zafClient,
  ZENDESK_FIELD_IDS,
  popupSuccess,
  popupError,
} from "../../services/Zendesk";
import { useZendeskData, formatDateBR } from "../../hooks";
import { useSideConversationContext } from "../../contexts/SideConversationContext";
import { 
  mapClassificacaoTroca, 
  mapPlataforma, 
  mapRetira, 
  mapStatusIntegracao, 
  mapAvaliacaoCredito,
  mapTipoRegistro,
  mapTipoEnvio
} from "../../utils/fieldMappings";



// Função para mapear valor para label mais amigável
const mapValueToLabel = (field: string, value: string): string => {
  const fieldLower = field.toLowerCase();
  
  if (fieldLower.includes('classificação da troca')) {
    return mapClassificacaoTroca(value);
  }
  
  if (fieldLower.includes('plataforma')) {
    return mapPlataforma(value);
  }
  
  if (fieldLower.includes('retira')) {
    return mapRetira(value);
  }
  
  if (fieldLower.includes('status da integração')) {
    return mapStatusIntegracao(value);
  }
  
  if (fieldLower.includes('avaliação de crédito')) {
    return mapAvaliacaoCredito(value);
  }
  
  if (fieldLower.includes('tipo do registro')) {
    return mapTipoRegistro(value);
  }
  
  if (fieldLower.includes('tipo de envio')) {
    return mapTipoEnvio(value);
  }
  
  // Para campos de data, aplicar formatação brasileira
  if (fieldLower.includes('data') && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return formatDateBR(value);
  }
  
  // Para campos boolean
  if (value === 'true' || value === 'Sim') return 'Sim';
  if (value === 'false' || value === 'Não') return 'Não';
  
  return value;
};

export interface PedidoData {
  id: string;
  title: string;
  fields: string[];
  values: string[];
  createdAt: string;
  products?: Array<{
    id: string; // ID do incident/ticket (para deletar)
    name: string;
    quantidade: string;
    preco: string;
    produtoId: string; // ID do custom object produto
    codigo?: string;
    marca?: string;
    linha?: string;
    peso?: string;
    altura?: string;
    largura?: string;
    profundidade?: string;
    ativo?: string;
    produtoComercial?: string;
    familiaProduto?: string;
    codigoErp?: string;
  }>;
}

export function useHome() {
  const navigate = useNavigate();
  const { ticketId } = useZendeskData();
  const { setSideConversationData } = useSideConversationContext();
  const [pedidos, setPedidos] = useState<PedidoData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Função para buscar produtos dos incidents relacionados a um side conversation específico
  const getProductsFromIncidents = async (sideConversationId?: string) => {
    if (!ticketId) return [];
    
    try {
      console.log(`🔍 DEBUG: Iniciando busca de produtos para side conversation: ${sideConversationId}`);
      
      let incidents: unknown[] = [];
      if (sideConversationId) {
        // Buscar incidents específicos desta side conversation usando a nova lógica aprimorada
        incidents = await getIncidentsBySideConversation(ticketId, sideConversationId);
        console.log(`📋 DEBUG: Encontrados ${incidents.length} incidents para side conversation ${sideConversationId}`);
      } else {
        // Fallback: retornar array vazio se não temos side conversation ID
        incidents = [];
        console.log(`⚠️ DEBUG: Sem side conversation ID, retornando array vazio`);
      }
      
      // IMPORTANTE: Se não encontrou incidents reais, retornar array vazio
      // Não vamos mais mostrar produtos mock/fallback
      if (incidents.length === 0) {
        console.warn(`⚠️ DEBUG: Nenhum incident encontrado para side conversation ${sideConversationId}`);
        return [];
      }
      
      console.log(`🔧 DEBUG: Processando ${incidents.length} incidents encontrados...`);
      
      const products = [];

      for (const incident of incidents) {
        const incidentData = incident as any;
        console.log(`🔍 Processing incident #${incidentData.id}: ${incidentData.subject}`);
        console.log(`   External ID: ${incidentData.external_id || 'N/A'}`);
        
        const details = await getIncidentDetails(incidentData.id);
        
        if (details && (details as any).custom_fields) {
          const fields = (details as any).custom_fields;
          
          // Debug: Mostrar todos os custom fields disponíveis
          console.log(`📋 DEBUG: Custom fields encontrados para incident #${incidentData.id}:`, 
            fields.map((f: any) => ({ id: f.id, value: f.value }))
          );
          
          console.log(`📋 IDs que estamos procurando:`, {
            quantidade: ZENDESK_FIELD_IDS.quantidade,
            preco: ZENDESK_FIELD_IDS.preco,
            produtoObjeto: ZENDESK_FIELD_IDS.produtoObjeto
          });
          
          // Buscar os campos de produto pelos IDs corretos
          const qty = fields.find((f: any) => f.id === ZENDESK_FIELD_IDS.quantidade)?.value;
          const price = fields.find((f: any) => f.id === ZENDESK_FIELD_IDS.preco)?.value;
          const productId = fields.find((f: any) => f.id === ZENDESK_FIELD_IDS.produtoObjeto)?.value;
          
          console.log(`🔧 DEBUG: Valores extraídos do incident #${incidentData.id}:`, {
            quantidade: qty,
            preco: price,
            produtoId: productId
          });
          
          // Se temos pelo menos um dos campos de produto, processar
          if (qty !== undefined || price !== undefined || productId !== undefined) {
            console.log(`✅ DEBUG: Incident #${incidentData.id} tem dados de produto válidos`);
            
            // Criar dados base do produto usando dados do incident
            let productData = {
              name: incidentData.subject || `Produto do Incident #${incidentData.id}`,
              codigo: '',
              marca: '',
              linha: '',
              peso: '',
              altura: '',
              largura: '',
              profundidade: '',
              ativo: 'Sim',
              produto_comercial: '',
              familia_produto: '',
              codigo_erp: ''
            };
            
            // Se temos productId, buscar dados completos do Custom Object
            if (productId) {
              try {
                
                // Tentar diferentes URLs da API do Custom Object
                const possibleUrls = [
                  `/api/v2/custom_objects/zen:custom_object:produto/records/${productId}`,
                  `/api/v2/custom_objects/produto/records/${productId}`,
                  `/api/v2/custom_objects/records/${productId}?object_type=produto`
                ];
                
                let productResponse: any = null;
                
                for (const url of possibleUrls) {
                  try {
                    productResponse = await zafClient.request({
                      url: url,
                      type: "GET",
                    });
                    break;
                  } catch {
                    continue;
                  }
                }
                
                if (!productResponse) {
                  throw new Error('Não foi possível acessar o Custom Object');
                }
                
                
                // Tentar diferentes estruturas de resposta
                let productFields = null;
                
                // Estrutura 1: custom_object_record.custom_object_fields
                if (productResponse.custom_object_record?.custom_object_fields) {
                  productFields = productResponse.custom_object_record.custom_object_fields;
                }
                // Estrutura 2: record.custom_object_fields  
                else if (productResponse.record?.custom_object_fields) {
                  productFields = productResponse.record.custom_object_fields;
                }
                // Estrutura 3: custom_object_fields direto
                else if (productResponse.custom_object_fields) {
                  productFields = productResponse.custom_object_fields;
                }
                // Estrutura 4: fields
                else if (productResponse.fields) {
                  productFields = productResponse.fields;
                }
                
                if (productFields) {
                  
                  // Log de cada campo individual para debug
                  
                  // Mapear os campos do produto baseado na estrutura que você mostrou
                  productData = {
                    name: productFields.nome || productFields.name || productFields.Nome || productData.name,
                    codigo: productFields.codigo_do_produto || productFields.codigo || productFields.Codigo || '',
                    marca: productFields.marca || productFields.Marca || '',
                    linha: productFields.linha || productFields.Linha || '',
                    peso: productFields.peso ? `${productFields.peso}` : '',
                    altura: productFields.altura ? `${productFields.altura}` : '',
                    largura: productFields.largura ? `${productFields.largura}` : '',
                    profundidade: productFields.profundidade ? `${productFields.profundidade}` : '',
                    ativo: productFields.ativo === false ? 'Não' : 'Sim',
                    produto_comercial: productFields.produto_comercial || productFields.ProdutoComercial || '',
                    familia_produto: productFields.familia_do_produto || productFields.familia_produto || productFields.FamiliaProduto || '',
                    codigo_erp: productFields.codigo_erp || productFields.CodigoERP || ''
                  };
                  
                } else {
                }
              } catch (productError) {
                console.warn(`❌ Erro ao buscar produto ${productId}:`, productError);
              }
            }
            
            // Criar produto baseado nos dados do incident + custom object  
            products.push({
              id: (incident as any).id?.toString() || 'sem-id', // ID do incident/ticket para poder deletá-lo
              incidentId: (incident as any).id?.toString() || 'sem-id', // ID do incident/ticket (backup/clareza)
              name: productData.name,
              quantidade: qty?.toString() || '1',
              preco: typeof price === 'number' ? `R$ ${price.toFixed(2).replace('.', ',')}` : `R$ ${price || '0,00'}`,
              produtoId: productId?.toString() || 'sem-id', // ID do custom object produto
              codigo: productData.codigo,
              marca: productData.marca,
              linha: productData.linha,
              peso: productData.peso,
              altura: productData.altura,
              largura: productData.largura,
              profundidade: productData.profundidade,
              ativo: productData.ativo,
              produtoComercial: productData.produto_comercial,
              familiaProduto: productData.familia_produto,
              codigoErp: productData.codigo_erp
            });
          } else {
          }
        } else {
        }
      }
      
      
      // Se encontramos incidents mas não conseguimos extrair produtos válidos,
      // criar produtos básicos com informações disponíveis dos incidents
      if (incidents.length > 0 && products.length === 0) {
        
        for (let i = 0; i < incidents.length; i++) {
          const incident = incidents[i] as any;
          
          // Tentar extrair informações básicas do incident
          const incidentId = incident.id || (i + 1);
          const subject = incident.subject || `Incident ${incidentId}`;
          const createdAt = incident.created_at ? new Date(incident.created_at).toLocaleDateString('pt-BR') : 'Data não disponível';
          
          products.push({
            id: incidentId?.toString() || 'sem-id', // ID do incident/ticket para poder deletá-lo
            incidentId: incidentId?.toString() || 'sem-id', // ID do incident/ticket (backup/clareza)
            name: `Produto - ${subject}`,
            quantidade: "1",
            preco: "Aguardando identificação", 
            produtoId: `incident-${incidentId}`, // ID do custom object produto
            codigo: `INC-${incidentId}`,
            marca: "Aguardando identificação",
            linha: "Aguardando identificação",
            peso: "-",
            altura: "-", 
            largura: "-",
            profundidade: "-",
            ativo: "Sim",
            produtoComercial: `Criado em ${createdAt}`,
            familiaProduto: "Incident",
            codigoErp: `ERP-${incidentId}`
          });
        }
      }
      
      return products;
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      return [];
    }
  };

  const loadSideConversations = async () => {
    if (!ticketId) {
      setError("Ticket ID não encontrado");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // DEBUG: Verificar que ticket ID temos
      console.log("🔍 DEBUG: === INICIANDO BUSCA DE DADOS ===");
      console.log(`🎫 Ticket ID: ${ticketId}`);

      const sideConversations = await getSideConversations(ticketId);

      // Converter side conversations para formato de pedidos
      const pedidosData: PedidoData[] = await Promise.all(
        sideConversations.map(async (sc, index) => {
          try {

            // Tentar usar primeiro os dados que já temos na side conversation
            let messageBody = "";
            let pedidoTitle = `Pedido ${index + 1}`;

            // Primeira tentativa: usar preview text diretamente do sc (mais rápido)
            if (sc.preview?.text) {
              messageBody = sc.preview.text;
            }

            // Segunda tentativa: buscar detalhes se não temos preview text
            if (!messageBody) {
              const details = await getSideConversationDetails(ticketId, sc.id);

              // Tentar preview_text dos detalhes
              if (details?.side_conversation?.preview_text) {
                messageBody = details.side_conversation.preview_text;
                console.log(
                  "Message body from details.preview_text:",
                  messageBody
                );
              }

              // Extrair title do subject se disponível
              const subject = details?.side_conversation?.subject || sc.subject;
              if (subject) {
                // Extrair nome do pedido do subject (ex: "Novo Pedido - teste" -> "Pedido: teste")
                const match = subject.match(/Novo Pedido\s*-\s*(.+)/i);
                if (match && match[1]) {
                  pedidoTitle = `Pedido: ${match[1]}`;
                }
              }

              // Fallback para messages se ainda não temos dados
              if (!messageBody) {
                const messages =
                  details?.side_conversation?.messages ||
                  details?.messages ||
                  [];
                if (messages.length > 0) {
                  messageBody = messages[0].body || messages[0].message || "";
                }
              }
            }

            console.log(
              "Message body preview:",
              messageBody?.substring(0, 200)
            );

            // Verificar se o preview_text está truncado (termina sem quebra de linha)
            const isTruncated =
              messageBody &&
              (messageBody.includes("...") ||
                (messageBody.length > 100 &&
                  !messageBody.endsWith("\n") &&
                  !messageBody.includes("*Criado em:")));


            // Se parece truncado ou muito pequeno, tentar buscar mensagem completa
            if (isTruncated || (messageBody && messageBody.length < 500)) {

              try {
                const fullMessage = await getFullSideConversationMessage(
                  ticketId,
                  sc.id
                );
                if (fullMessage && fullMessage.length > messageBody.length) {
                  console.log(
                    `Encontrada mensagem mais completa! ${messageBody.length} -> ${fullMessage.length} caracteres`
                  );
                  messageBody = fullMessage;
                }
              } catch (altError) {
              }
            }

            // Se ainda não temos dados suficientes, criar dados básicos
            if (!messageBody || messageBody.length < 10) {
              const subject = sc.subject || "Pedido sem título";
              const createdDate = new Date(sc.created_at).toLocaleString(
                "pt-BR"
              );

              messageBody = `**Pedido:** ${subject}\n`;
              messageBody += `**Status:** Dados disponíveis parcialmente\n`;
              messageBody += `**Criado em:** ${createdDate}\n`;
              messageBody += `**ID da Side Conversation:** ${sc.id}\n`;

              // Adicionar informações dos participantes se disponíveis
              if (sc.participants && sc.participants.length > 0) {
                messageBody += `**Participantes:** ${sc.participants
                  .map((p) => p.name || `User ${p.user_id}`)
                  .join(", ")}\n`;
              }
            }

            // Parse dos dados do pedido do corpo da mensagem
            const pedidoData = parsePedidoFromMessage(messageBody);

            // Buscar produtos específicos desta side conversation
            const products = await getProductsFromIncidents(sc.id);
            
            // Log detalhado dos produtos
            console.log(`📦 DEBUG: Produtos encontrados para SC ${sc.id}:`, products.length);
            if (products.length > 0) {
              products.forEach((product, index) => {
                console.log(`📦 Produto ${index + 1} da SC ${sc.id}:`, {
                  name: product.name,
                  quantidade: product.quantidade,
                  preco: product.preco,
                  id: product.id
                });
              });
            } else {
              console.warn(`⚠️ DEBUG: Nenhum produto encontrado para SC ${sc.id}. Verificar se há incidents relacionados.`);
            }

            return {
              id: sc.id,
              title: pedidoTitle,
              fields: pedidoData.fields,
              values: pedidoData.values,
              createdAt: sc.created_at,
              products,
            };
          } catch (error) {
            console.error(
              `Erro ao buscar detalhes da side conversation ${index + 1}:`,
              error
            );
            return {
              id: sc.id,
              title: `Pedido ${index + 1}`,
              fields: ["Status"],
              values: ["Erro ao carregar dados"],
              createdAt: sc.created_at,
            };
          }
        })
      );

      setPedidos(pedidosData);
      
      // Salvar dados no contexto para uso na página Order
      pedidosData.forEach(pedido => {
        const sideConversationData = {
          id: pedido.id,
          title: pedido.title,
          fields: pedido.fields,
          values: pedido.values,
          createdAt: pedido.createdAt,
          state: "open", // Assumindo que só mostramos abertos na Home
          subject: pedido.title,
          ticket_id: ticketId || 0,
          products: pedido.products?.map(product => ({
            id: product.id, // ID do incident/ticket (para deletar)
            incidentId: product.id, // ID do incident/ticket (backup/clareza)
            name: product.name,
            quantidade: product.quantidade,
            preco: product.preco,
            produtoId: product.produtoId, // ID do custom object produto
            codigo: product.codigo,
            marca: product.marca,
            linha: product.linha,
            peso: product.peso,
            altura: product.altura,
            largura: product.largura,
            profundidade: product.profundidade,
            ativo: product.ativo,
            produtoComercial: product.produtoComercial,
            familiaProduto: product.familiaProduto,
            codigoErp: product.codigoErp,
            imageUrl: "" // Será buscado na página Order se necessário
          })) || []
        };
        
        setSideConversationData(pedido.id, sideConversationData);
      });
      
      setError(null);
    } catch (error) {
      console.error("Erro ao carregar side conversations:", error);
      setError("Erro ao carregar pedidos");
      setPedidos([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para buscar mensagem completa de uma side conversation
  const getFullSideConversationMessage = async (
    ticketId: number,
    sideConversationId: string
  ): Promise<string> => {

    const endpoints = [
      `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}?include=messages`,
      `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}/events`,
      `/api/v2/side_conversations/${sideConversationId}/events`,
      `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}/messages`,
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await zafClient.request({
          url: endpoint,
          type: "GET",
        });


        // Tentar extrair mensagem - abordagem mais simples
        const responseStr = JSON.stringify(response);
        console.log(
          `Resposta como string (primeiros 500 chars):`,
          responseStr.substring(0, 500)
        );

        // Buscar por padrões que indiquem uma mensagem completa
        const messagePatterns = [
          /"body"\s*:\s*"([^"]*\*\*Novo Pedido Criado\*\*[^"]*)"/,
          /"message"\s*:\s*"([^"]*\*\*Novo Pedido Criado\*\*[^"]*)"/,
          /"text"\s*:\s*"([^"]*\*\*Novo Pedido Criado\*\*[^"]*)"/,
        ];

        for (const pattern of messagePatterns) {
          const match = responseStr.match(pattern);
          if (match && match[1] && match[1].length > 100) {
            // Decodificar caracteres escapados
            const decodedMessage = match[1]
              .replace(/\\n/g, "\n")
              .replace(/\\"/g, '"')
              .replace(/\\\\/g, "\\");

            console.log(
              `Mensagem completa encontrada via regex em ${endpoint}:`,
              decodedMessage.length,
              "caracteres"
            );
            return decodedMessage;
          }
        }
      } catch (error) {
        continue;
      }
    }

    return "";
  };

  // Função para fazer parse dos dados do pedido a partir da mensagem
  const parsePedidoFromMessage = (
    messageBody: string
  ) => {
    const fieldLabels: string[] = [];
    const values: string[] = [];


    try {
      // Se não há mensagem, retornar dados não disponíveis
      if (!messageBody || messageBody.trim().length === 0) {
        fieldLabels.push("Status");
        values.push("Dados não disponíveis");
        return { fields: fieldLabels, values };
      }

      // Extrair informações usando regex com diferentes variações
      const patterns = {
        Estabelecimento: [
          /\*\*Estabelecimento:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Estabelecimento:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Tipo de Envio": [
          /\*\*Tipo de Envio:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Tipo de Envio:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Pedido Interno": [
          /\*\*Pedido Interno:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Pedido Interno:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Classificação da Troca": [
          /\*\*Classificação da Troca:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Classificação da Troca:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Avaliação de Crédito": [
          /\*\*Avaliação de Crédito:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Avaliação de Crédito:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        Plataforma: [
          /\*\*Plataforma:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Plataforma:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        Retira: [
          /\*\*Retira:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Retira:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Número de Parcelas": [
          /\*\*Número de Parcelas:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Número de Parcelas:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "ECOM PJ": [
          /\*\*ECOM PJ:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /ECOM PJ:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Status da Integração": [
          /\*\*Status da Integração:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Status da Integração:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Tipo do Registro": [
          /\*\*Tipo do Registro:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Tipo do Registro:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Data de Entrega": [
          /\*\*Data de Entrega:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Data de Entrega:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
        "Data da Integração": [
          /\*\*Data da Integração:\*\*\s*(.+?)(?=\n|\*\*|$)/i,
          /Data da Integração:\s*(.+?)(?=\n|\*\*|$)/i,
        ],
      };

      // Extrair cada campo testando diferentes padrões
      Object.entries(patterns).forEach(([label, regexList]) => {
        for (const pattern of regexList) {
          const match = messageBody.match(pattern);
          if (match && match[1]) {
            const value = match[1].trim();
            if (value && value !== "") {
              const formattedValue = mapValueToLabel(label, value);
              fieldLabels.push(label);
              values.push(formattedValue);
              break; // Sai do loop ao encontrar o primeiro match
            }
          }
        }
      });

      // Extrair informações dos produtos
      const produtosPatterns = [
        /\*\*Produtos:\*\*([\s\S]*?)(?=\*\*|$)/i,
        /Produtos:([\s\S]*?)(?=\*\*|$)/i,
      ];

      for (const pattern of produtosPatterns) {
        const produtosMatch = messageBody.match(pattern);
        if (produtosMatch && produtosMatch[1]) {
          const produtos = produtosMatch[1].trim();
          if (produtos) {
            fieldLabels.push("Produtos");
            values.push(produtos.replace(/\n/g, " | "));
            break;
          }
        }
      }

      // Extrair observações se existirem
      const observacoesPatterns = [
        /\*\*Observações:\*\*([\s\S]*?)(?=\*\*|$)/i,
        /Observações:([\s\S]*?)(?=\*\*|$)/i,
      ];

      for (const pattern of observacoesPatterns) {
        const observacoesMatch = messageBody.match(pattern);
        if (observacoesMatch && observacoesMatch[1]) {
          const observacoes = observacoesMatch[1].trim();
          if (observacoes) {
            fieldLabels.push("Observações");
            values.push(observacoes);
            break;
          }
        }
      }

      console.log("Values:", values); // Se não conseguiu extrair nenhum campo, tentar uma abordagem mais simples
      if (fieldLabels.length === 0) {

        // Tentar extrair qualquer linha que pareça ser um campo de dados
        const lines = messageBody.split("\n");
        lines.forEach((line) => {
          // Tentar diferentes padrões de linha
          const patterns = [
            /^\*\*(.+?):\*\*\s*(.+)$/, // **Campo:** valor
            /^(.+?):\s*(.+)$/, // Campo: valor
            /^\*\*(.+?)\*\*\s+(.+)$/, // **Campo** valor
          ];

          for (const pattern of patterns) {
            const match = line.match(pattern);
            if (match && match[1] && match[2]) {
              const field = match[1].trim();
              const value = match[2].trim();

              // Evitar duplicatas e campos vazios
              if (field && value && !fieldLabels.includes(field)) {
                const formattedValue = mapValueToLabel(field, value);
                fieldLabels.push(field);
                values.push(formattedValue);
                break;
              }
            }
          }
        });

        // Se ainda não encontrou nada, mostrar conteúdo resumido
        if (fieldLabels.length === 0) {
          console.log(
            "Nenhum campo estruturado encontrado, mostrando conteúdo bruto"
          );
          fieldLabels.push("Conteúdo da Mensagem");
          values.push(
            messageBody.substring(0, 200) +
              (messageBody.length > 200 ? "..." : "")
          );
        }
      }
    } catch (error) {
      console.error("Erro ao fazer parse da mensagem:", error);
      fieldLabels.push("Status");
      values.push("Erro ao processar dados");
    }

    return { fields: fieldLabels, values };
  };  // Função para deletar um pedido (side conversation)
  const deletePedido = async (pedidoId: string, pedidoTitle: string) => {
    
    if (!ticketId) {
      return;
    }

    try {

      // Deletar a side conversation (sem confirmação por enquanto devido ao sandboxing)
      await deleteSideConversation(ticketId, pedidoId);


      // Mostrar mensagem de sucesso
      popupSuccess(`Pedido "${pedidoTitle}" fechado com sucesso!`, true);


      // Recarregar a lista de pedidos
      await loadSideConversations();


    } catch (error) {
      console.error('❌ Erro ao deletar pedido:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      popupError(`Erro ao remover pedido: ${errorMessage}`, false);
    }
  };

  useEffect(() => {
    loadSideConversations();
  }, [ticketId]);

  return {
    navigate,
    pedidos,
    isLoading,
    error,
    refetch: loadSideConversations,
    deletePedido,
  };
}
